import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../../services/auth/authService';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract code from URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');

        console.log('OAuth callback received');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        // Verify state
        const storedState = sessionStorage.getItem('oauth_state');
        const storedVerifier = sessionStorage.getItem('oauth_code_verifier');

        // If PKCE artifacts are missing (e.g., user hit back after logout), restart login
        if (!storedState || !storedVerifier) {
          console.warn('PKCE state/verifier missing — likely stale session. Restarting login...');
          // Clear any partial state
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_code_verifier');
          // Redirect to login to start fresh PKCE flow
          navigate('/login', { replace: true });
          return;
        }

        if (state !== storedState) {
          console.warn('State mismatch — possible CSRF or stale session. Restarting login...');
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_code_verifier');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange code for tokens (frontend does this, not backend)
        const { accessToken, refreshToken, idToken, userId } =
          await authService.exchangeCodeForToken(code);

        // Store tokens in sessionStorage
        sessionStorage.setItem('accessToken', accessToken);
        sessionStorage.setItem('refreshToken', refreshToken);
        sessionStorage.setItem('idToken', idToken);
        sessionStorage.setItem('userId', userId);

        // Clean up sessionStorage
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code_verifier');

        // Clean up URL
        window.history.replaceState({}, document.title, '/');

        // Determine post-login redirect (validate to avoid open-redirect)
        let redirectTo = '/';
        try {
          const stored = sessionStorage.getItem('postLoginRedirect') || '';
          if (stored?.startsWith('/')) {
            redirectTo = stored;
          }
          // Remove the stored value once consumed
          if (stored) sessionStorage.removeItem('postLoginRedirect');
        } catch (e) {
          console.warn('Failed to read postLoginRedirect, falling back to root', e);
        }

        // Navigate to the saved path (or fallback)
        navigate(redirectTo, { replace: true });
      } catch (error) {
        console.error('Callback handling failed:', error);
        // On any error, redirect to login for a clean start
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>Processing authentication...</h2>
      <p>Redirecting you to the dashboard...</p>
    </div>
  );
};

export default AuthCallback;